import React, { useEffect, useRef, useState } from 'react';
import { Popover, Typography, Box, Tabs, styled, Tab, InputAdornment, TextField, Grid2, Button } from '@mui/material';
import { CancelFormActionButton, PrimaryFormActionButton } from '../../../Features/Table/GenericDraggableTable/TableStyles';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from '../../../Helper/Common/common_helpers';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { genericSearchSuggestions, getDigitalTwinList } from '../../../Api/DigitalTwinListApi';
import CartLoader from '../../../Helper/Loaders/CartLoader';
import HelpPopup from './HelpPopup';
import { usePagesContent } from '../../../Hooks/usePagesContent';
import ChangeDeviceAlertDialog from '../Common/ChangeDeviceAlertDialog';
import { getOrganizationLangSettings } from '../../../Helper/Organization/Functions';

const styles = {
	popoverPaper: {
		width: 360,
		borderRadius: '10px',
		backgroundColor: '#FFFFFF',
		boxShadow: '-2px -2px 4px 0px rgba(151, 151, 151, 0.25), 2px 2px 4px 4px rgba(151, 151, 151, 0.25)',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		borderBottom: '1px solid #E0E0E0',
		padding: '12px 16px 12px 12px',
		justifyContent: 'space-between'
	},
	headerText: {
		fontFamily: 'Roboto',
		fontWeight: 700,
		fontSize: '14px',
		lineHeight: '24px',
		color: '#3D4B5A',
		px: 1,
	},
	footer: {
		p: 1,
		borderTop: '1px solid #E0E0E0',
		display: 'flex',
		justifyContent: 'flex-end',
	},
};

const StyledFilterTab = styled((props) => <Tab disableRipple {...props} />)(
	({ theme, isFilterApplied }) => ({
		textTransform: "none",
		minWidth: 0,
		minHeight: "10px",
		width: "100%",
		textAlign: "left",
		fontFamily: "Roboto",
		fontSize: "12px",
		fontWeight: 500,
		padding: "4px 8px 4px 8px",
		borderRadius: "8px",
		transition: "background 0.2s ease-in-out, color 0.2s ease-in-out",
		alignItems: "start",
		marginBottom: "2px",
		color: "#3D4B5A",
		position: "relative",

		"&:hover": {
			background: "#F7F7F7",
			color: "#3D4B5A",
		},
		"&.Mui-selected": {
			background: "#EEEFFF",
			color: "#6D72F6",
			fontWeight: "bold",
		},

		// Custom left highlighter
		...(isFilterApplied && {
			"&::before": {
				content: '""',
				position: "absolute",
				left: 0,
				top: "25%",
				height: "50%",
				width: "2px",
				backgroundColor: "#6D72F6",
				borderRadius: "2px",
			},
		}),
	})
);

const StyledSearchInput = styled(TextField)(({ theme }) => ({
	width: "100%",
	"& .MuiOutlinedInput-root": {
		borderRadius: "12px",
		paddingLeft: "0px",
	},
	"& fieldset": {
		border: "none !important",
	},
	"&:hover fieldset": {
		border: "none !important",
	},
	"&.Mui-focused fieldset": {
		border: "none !important",
	},
	"& .MuiInputBase-input": {
		padding: 0,
		fontSize: "12px !important",
		fontWeight: 400,
		border: "none !important",
		caretColor: "#5D5D5D",
		"&::placeholder": {
			fontSize: "12px",
			color: "#ADB5BE",
			opacity: 1,
		},
	},
}));

const FilterBody = ({ filters = {}, selectedEntity, setSelectedEntity }) => {
	const [selectedTab, setSelectedTab] = useState(0);
	const initialFilters = useRef({ ...filters }).current;
	const [filterOptions, setFilterOptions] = useState([]);
	const { t } = useTranslation(['common', 'generic_table', 'filters']);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		if (selectedEntity?.type && filterOptions.length > 0) {
			const index = filterOptions.findIndex(
				(f) => f.key === selectedEntity.type
			);
			if (index >= 0) {
				setSelectedTab(index);
			}
		}
	}, [selectedEntity, filterOptions]);

	const searchForEntities = async () => {
		if (!searchTerm) {
			if (initialFilters && Object.keys(initialFilters).length > 0) {
				setFilterOptions(Object.values(initialFilters));
			}
			return;
		}
		const searchField = filterOptions[selectedTab].key;
		const searchSuggestionsResponse = await genericSearchSuggestions(searchTerm, searchField, 10);
		const searchSuggestions = searchSuggestionsResponse?.response?.data || [];
		const updatedOptions = []
		searchSuggestions.forEach(suggestion => {
			updatedOptions.push({
				"id": suggestion,
				"value": suggestion,
			})
		});
		const modifiedFilterOptions = filterOptions.map((option, idx) =>
			idx === selectedTab ? { ...option, values: updatedOptions } : option
		);
		setFilterOptions(modifiedFilterOptions);
	}

	useEffect(() => {
		if (filters && Object.keys(filters).length > 0) {
			setFilterOptions(Object.values(filters));
		}
	}, [filters]);

	useEffect(() => {
		searchForEntities();
	}, [searchTerm]);

	if (Object.keys(filters).length <= 0) {
		return (
			<Box sx={{ height: '232px' }}>
				<CartLoader />
			</Box>
		);
	}

	return (
		<Box sx={{ display: 'flex' }}>
			<Tabs
				orientation="vertical"
				variant="scrollable"
				visibleScrollbar={true}
				scrollButtons={false}
				value={selectedTab}
				onChange={(event, newValue) => setSelectedTab(newValue)}
				sx={{
					borderRight: 1,
					borderColor: 'divider',
					width: '130px',
					height: '216px',
					padding: '8px'
				}}
				TabIndicatorProps={{
					style: { display: 'none' },
				}}
			>
				{filterOptions.map((filter, index) => (
					<StyledFilterTab
						key={index}
						label={
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									width: '100%',
								}}
							>
								<Typography variant={selectedTab === index ? "fw500s12lh24c6D72F6" : "fw400s12lh24c3D4B5A"}> {
									t(`filters:${normalizeKey(filter.label)}`, filter.label)
								} </Typography>
								{selectedTab === index && (
									<ArrowForwardIosIcon
										sx={{
											fontSize: '12px',
											fontWeight: 500,
											color: '#6D72F6',
											marginLeft: 'auto',
										}}
									/>
								)}
							</Box>
						}
					/>
				))}
			</Tabs>
			<Box sx={{ flexGrow: 1, padding: '8px 16px 8px 12px', height: '216px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
				<Box>
					<Box sx={{
						width: "100%",
						mb: 1,
						py: 0.5,
						paddingRight: '0px',
						border: "1px solid #EBEFF3",
						borderRadius: "12px"
					}}>
						<StyledSearchInput
							placeholder={` ${t('search_placeholder')} ${filterOptions[selectedTab]?.label}`}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start" sx={{ marginRight: '4px', marginLeft: '8px' }}>
											<SearchIcon sx={{ color: "#808080", fontSize: '18px', p: 0 }} />
										</InputAdornment>
									),
								}
							}}
						/>
					</Box>
					<Box sx={{ height: '180px', overflow: 'auto', width: '100%' }}>
						<Grid2 container flexDirection={'column'} gap={0.5}>
							{filterOptions[selectedTab]?.values.filter((v) => v.value).map((filterValue, index) => {
								const isSelected = filterOptions[selectedTab]?.key === selectedEntity?.type && filterValue.value === selectedEntity?.value;
								return (
									<Typography
										key={index}
										variant={isSelected ? "fw500s12lh24c6D72F6" : "fw400s12lh24c3D4B5A"}
										sx={{
											padding: '2px 8px',
											borderRadius: '4px',
											cursor: 'pointer',
											background: isSelected ? '#EEEFFF' : '',
											color: isSelected ? '#6D72F6' : '',
											...(!isSelected && {
												"&:hover": {
													background: "#F7F7F7",
												},
											})
										}}
										onClick={() => {
											setSelectedEntity({
												...filterValue,
												'type': filterOptions[selectedTab]?.key,
											})
										}}>
										{filterValue.value}
									</Typography>
								)
							})}
						</Grid2>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default function EntityFilter({
	handleEntitySelection = () => { },
	defaultEntity = null
}) {

	const orgLangSettings = getOrganizationLangSettings();

	const { t } = useTranslation(['common', 'generic_table']);
	const entityLabel = orgLangSettings?.digitalTwinSingleLabel ? t(orgLangSettings.digitalTwinSingleLabel) : t('inventory:tabs.battery_config.single_label');

	const [filters, setFilters] = useState([]);
	const pagesContent = usePagesContent();
	const [previousEntity, setPreviousEntity] = useState(defaultEntity || {})
	const [selectedEntity, setSelectedEntity] = useState(defaultEntity || {});
	const [selectedEntityLabel, setSelectedEntityLabel] = useState(null);
	const [showDeviceChangeAlert, setShowDeviceChangeAlert] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [showHelp, setShowHelp] = useState(true);

	useEffect(() => {
		if (defaultEntity && defaultEntity.value) {
			setSelectedEntityLabel(defaultEntity.value);
			setSelectedEntity(defaultEntity);
			setPreviousEntity(defaultEntity);
			setShowHelp(false);
		} else {
			setSelectedEntity({});
			setSelectedEntityLabel(null);
			setShowHelp(true);
		}
	}, [defaultEntity]);

	useEffect(() => {
		if (defaultEntity && defaultEntity.value && Object.keys(filters).length > 0) {
			setSelectedEntity({
				id: defaultEntity.id,
				value: defaultEntity.value,
				type: defaultEntity.type || "deviceID",
			});
		}
	}, [defaultEntity, filters]);

	const open = Boolean(anchorEl);
	const id = open ? 'filter-popover' : undefined;

	const openFilterMenu = (event, key = null) => {
		setAnchorEl(event.currentTarget);
		setShowHelp(false);
	};

	const closeFilterMenu = () => {
		setAnchorEl(null);
	};

	const onEntitySelection = async () => {

		// fetch batteryID based on suggestion selectedEntity.id
		const digitalTwinListResponse = await getDigitalTwinList({
			filters: [],
			pageNumber: 1,
			rowsPerPage: 20,
			startDate: null,
			endDate: null,
			[selectedEntity?.type]: selectedEntity?.value,
			columns: ["batteryID"],
		});

		const digitalTwinList = digitalTwinListResponse?.response?.data || [];
		if (!digitalTwinList.length) {
			return;
		}
		const digitalTwin = digitalTwinList[0];

		handleEntitySelection({
			...selectedEntity,
			id: digitalTwin.batteryID,
		});
		setSelectedEntityLabel(selectedEntity?.value);
		setPreviousEntity(selectedEntity);
		setAnchorEl(null)
	}

	const handleApplyAction = () => {
		if (previousEntity?.id) {
			setShowDeviceChangeAlert(true);
			return;
		}
		setShowDeviceChangeAlert(false);
		onEntitySelection();
	}

	const shouldDisableApply = () => {
		return !selectedEntity?.value;
	}

	const fetchFiltersData = async () => {
		const digitalTwinListResponse = await getDigitalTwinList({
			filters: [],
			pageNumber: 1,
			rowsPerPage: 20,
			startDate: null,
			endDate: null,
			columns: ["licensePlateNo", "batteryID", "vin", "deviceID"],
		});
		const digitalTwinList = digitalTwinListResponse?.response?.data || [];
		const filtersConfig = {
			"licensePlateNo": {
				"key": "licensePlateNo",
				"label": "Bus Number",
				"values": []
			},
			"deviceID": {
				"key": "deviceID",
				"label": t('common:device_id'),
				"values": []
			},
			"vin": {
				"key": "vin",
				"label": "Chassis Number",
				"values": []
			},
		}
		digitalTwinList.forEach(digitalTwin => {
			Object.keys(filtersConfig).forEach((configKey) => {
				filtersConfig[configKey].values.push({
					"id": digitalTwin?.batteryID,
					"value": digitalTwin?.[configKey]
				});
			});
		})
		const clientFilters = getClientEntityTypeFilters(filtersConfig);
		setFilters(clientFilters);
	}

	const getClientEntityTypeFilters = (currentFilters) => {
		if (!currentFilters) {
			return currentFilters;
		}

		const clientEntityTypes = pagesContent.metaInfo?.entityTypes || [];
		if (!clientEntityTypes || clientEntityTypes.length === 0) {
			return { ...currentFilters };
		}

		let results = {}
		for (const key of clientEntityTypes) {
			results[key] = { ...currentFilters[key] || {} };
		}

		return results;
	}

	useEffect(() => {
		fetchFiltersData();
	}, []);

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
			<Button
				variant="outlined"
				onClick={openFilterMenu}
				endIcon={<ArrowDropDownIcon sx={{ color: open ? "#6D72F6" : '#3D4B5A' }} />}
				sx={{
					borderColor: open ? "#6D72F6" : '#E6E6E6',
					borderRadius: "8px",
					textTransform: 'none',
					padding: "6px 16px",
					"&:hover": {
						borderColor: "#6D72F6",
						backgroundColor: "rgba(108, 99, 255, 0.05)",
					},
				}}
			>
				<Typography variant="fw400s12c3D4B5A" sx={{ lineHeight: '20px', color: open ? '#6D72F6' : '#3D4B5A' }}>
					{selectedEntityLabel ? selectedEntityLabel : `Select a ${entityLabel}`}
				</Typography>
			</Button>

			<HelpPopup open={showHelp} />

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={closeFilterMenu}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				slotProps={{ paper: { sx: styles.popoverPaper } }}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={styles.header}>
						<Typography sx={{ mx: 1 }} variant="fw700s14lh24c3D4B5A">Select an Entity</Typography>
					</Box>
					<FilterBody
						filters={filters}
						selectedEntity={selectedEntity}
						setSelectedEntity={setSelectedEntity}
					/>
					<Box sx={styles.footer}>
						<CancelFormActionButton onClick={closeFilterMenu}> {t('actions.cancel')} </CancelFormActionButton>
						<PrimaryFormActionButton onClick={handleApplyAction} disabled={shouldDisableApply()} >
							{t('actions.apply')}
						</PrimaryFormActionButton>
					</Box>
				</Box>
			</Popover>
			<ChangeDeviceAlertDialog
				open={showDeviceChangeAlert}
				onClose={() => setShowDeviceChangeAlert(false)}
				onConfirm={() => {
					setShowDeviceChangeAlert(false);
					onEntitySelection();
				}}
			/>
		</Box>
	);
}
